import api from './api';

export enum IntegrationStatus {
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  ERROR = 'error',
  SYNCING = 'syncing',
}

export interface IntegrationInfo {
  id: string;
  name: string;
  description: string;
  status: IntegrationStatus;
  lastSync: Date | null;
  lastError: string | null;
  isConfigured: boolean;
  requiresAuth: boolean;
  config: any;
}

export interface IntegrationConfig {
  apiKey?: string;
  apiSecret?: string;
  baseUrl?: string;
  username?: string;
  password?: string;
  clientId?: string;
  clientSecret?: string;
  jwt?: string;
  [key: string]: any;
}

export interface IntegrationLog {
  id: string;
  integrationId: string;
  integrationSystem: string;
  operation: string;
  status: string;
  errorMessage?: string;
  startedAt: Date;
  completedAt?: Date;
  userId?: string;
}

class IntegrationService {
  private baseUrl = '/api/integrations';

  /**
   * Get all integrations
   */
  async getIntegrations(): Promise<IntegrationInfo[]> {
    const response = await api.get(this.baseUrl);
    return response.data;
  }

  /**
   * Get a specific integration
   */
  async getIntegration(id: string): Promise<IntegrationInfo> {
    const response = await api.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Get integration status
   */
  async getIntegrationStatus(id: string): Promise<{ integrationId: string; status: IntegrationStatus }> {
    const response = await api.get(`${this.baseUrl}/${id}/status`);
    return response.data;
  }

  /**
   * Connect an integration
   */
  async connectIntegration(id: string, config: IntegrationConfig): Promise<IntegrationInfo> {
    const response = await api.post(`${this.baseUrl}/${id}/connect`, { config });
    return response.data;
  }

  /**
   * Disconnect an integration
   */
  async disconnectIntegration(id: string): Promise<IntegrationInfo> {
    const response = await api.post(`${this.baseUrl}/${id}/disconnect`);
    return response.data;
  }

  /**
   * Force sync an integration
   */
  async syncIntegration(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`${this.baseUrl}/${id}/sync`);
    return response.data;
  }

  /**
   * Test integration connection
   */
  async testConnection(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`${this.baseUrl}/${id}/test`);
    return response.data;
  }

  /**
   * Get integration logs
   */
  async getIntegrationLogs(id: string, limit = 50): Promise<IntegrationLog[]> {
    const response = await api.get(`${this.baseUrl}/${id}/logs?limit=${limit}`);
    return response.data;
  }

  /**
   * Get integration configuration template
   */
  getConfigurationTemplate(integrationId: string): IntegrationConfig {
    switch (integrationId) {
      case 'kroll':
        return {
          baseUrl: '',
          username: '',
          password: '',
        };
      case 'descartes':
        return {
          apiKey: '',
          baseUrl: '',
        };
      case 'ringcentral':
        return {
          clientId: '',
          clientSecret: '',
          jwt: '',
        };
      case 'pharmfax':
        return {
          apiKey: '',
        };
      case 'nexi':
        return {
          apiKey: '',
          baseUrl: '',
        };
      default:
        return {};
    }
  }

  /**
   * Validate integration configuration
   */
  validateConfiguration(integrationId: string, config: IntegrationConfig): string[] {
    const errors: string[] = [];

    switch (integrationId) {
      case 'kroll':
        if (!config.baseUrl) errors.push('Base URL is required');
        if (!config.username) errors.push('Username is required');
        if (!config.password) errors.push('Password is required');
        break;
      case 'descartes':
        if (!config.apiKey) errors.push('API Key is required');
        if (!config.baseUrl) errors.push('Base URL is required');
        break;
      case 'ringcentral':
        if (!config.clientId) errors.push('Client ID is required');
        if (!config.clientSecret) errors.push('Client Secret is required');
        if (!config.jwt) errors.push('JWT is required');
        break;
      case 'pharmfax':
        if (!config.apiKey) errors.push('API Key is required');
        break;
      case 'nexi':
        if (!config.apiKey) errors.push('API Key is required');
        if (!config.baseUrl) errors.push('Base URL is required');
        break;
    }

    return errors;
  }
}

export const integrationService = new IntegrationService();